import type { HttpErrorResponse } from '@angular/common/http';

import type { MemoizedSelector } from '@ngrx/store';
import { createSelector, createSelectorFactory, defaultMemoize } from '@ngrx/store';

import { UserSelectors } from '@core/account-data';
import { LocationSelectors } from '@core/locations-data';
import { createEntityCollectionStore, memoizeSelector, ngrxEvent } from '@core/ngrx-legacy';
import type { OfferBrief } from '@core/offers-types';

import { CanAssignToJobsWithStatuses, EntityStoreKey, OfferScope } from '../../../types';

const { actions, namespace, selectors, reducer } = createEntityCollectionStore<OfferBrief>()(
  EntityStoreKey.JobsBrief,
  {
    references: makeRef => [
      makeRef('locationIds', 'locations', LocationSelectors.byIds),
      makeRef('recruiterId', 'recruiter', UserSelectors.byId),
      makeRef('hiringManagerId', 'hiringManager', UserSelectors.byId),
    ],
    customActions: adapter => ({
      fetchCollection: (state, payload: { scope: OfferScope }) =>
        adapter.markAsPending(state, { scope: payload.scope, entities: [] }),
      fetchActiveCollection: state =>
        adapter.markAsPending(state, { scope: OfferScope.Active, entities: [] }),
      fetchArchivedCollection: state =>
        adapter.markAsPending(state, { scope: OfferScope.Archived, entities: [] }),
      fetchArchivedCollectionSuccess: ngrxEvent(),
      fetchArchivedCollectionFailed: ngrxEvent<{
        error: HttpErrorResponse;
      }>(),
      upsertOne: (state, payload: { data: OfferBrief }) => adapter.upsertOne(state, payload.data),
      upsertMany: (state, payload: { data: OfferBrief[]; scope: OfferScope }) =>
        adapter.upsertMany(state, payload.data, { scope: payload.scope }),
      references: (state, payload: { data: OfferBrief[] }) =>
        adapter.updateOrCreateMany(state, payload.data),
    }),
  },
);

// entities
const active = createSelector(selectors.entities({ scope: OfferScope.Active }), state => state);
const archived = createSelector(selectors.entities({ scope: OfferScope.Archived }), state => state);
const all = createSelector(active, archived, (active, archived) => [...active, ...archived]);
const canAssignTo = createSelector(selectors.entities({ scope: OfferScope.Active }), state =>
  state.filter(job => CanAssignToJobsWithStatuses.includes(job.status)),
);

// meta
const activeScopeIsLoaded = createSelector(
  selectors.isLoaded({ scope: OfferScope.Active }),
  state => state,
);
const archivedScopeIsLoaded = createSelector(
  selectors.isLoaded({ scope: OfferScope.Archived }),
  state => state,
);

const byIdsWithReferences = memoizeSelector(
  (ids: number[]): MemoizedSelector<any, OfferBrief[]> =>
    createSelectorFactory(defaultMemoize)(
      ...(ids || []).map(id => selectors.byIdWithReferences(id)),
      (...result: OfferBrief[]) => result.filter(offer => !!offer),
    ),
);

const bySlug = memoizeSelector((slug: string) =>
  createSelector(all, (state: OfferBrief[]) => state.find(job => job.slug === slug)),
);

const customSelectors = {
  all,
  active,
  archived,
  canAssignTo,
  activeScopeIsLoaded,
  archivedScopeIsLoaded,
  byIdsWithReferences,
  bySlug,
};

const jobsBriefSelectors = {
  ...selectors,
  ...customSelectors,
};

export {
  jobsBriefSelectors as JobsBriefSelectors,
  actions as JobsBriefActions,
  namespace as JobsBriefANamespace,
  reducer as JobsBriefReducer,
};
